import { Stack } from '@mui/material';
import FormNav from 'common/components/FormNav';
import useFlexBoxStyles from 'common/flexBox';
import LayoutForm from 'common/layouts/LayoutForm';
import { isBefore } from 'date-fns';
import { useFormFields } from 'libs/hooksLib';
import { isEqual } from 'lodash';
import * as moment from 'moment';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { cancelCloseCustomDialogAction, closeCustomDialogAction } from 'redux/slices/customDialogSlice';
import { cancelCloseNestedDialogAction, closeNestedDialogAction } from 'redux/slices/nestedDialogSlice';
import AppointmentFormSettings from './AppointmentFormSettings';
import AppointmentFormStep2 from './AppointmentFormStep2';

const AppointmentForm = (props) => {
    const { action, appointment, onSubmit, service } = props;
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const { classes: flexBox, cx } = useFlexBoxStyles();
    const dispatch = useDispatch();
    const [step, setStep] = useState(1);
    const [subForm, setSubForm] = useState(null);
    const confirmTitle = t('event.eventForm.deleteConfirm');
    const confirmMessage = t('event.eventForm.deleteMessage');
    const [actors, setActors] = useState([]);
    const [reminders, setReminders] = useState(appointment?.reminders || []);
    const [availabilitySelectedCalendars, setAvailabilitySelectedCalendars] = useState([]);

    const initialForm = useMemo(() => {
        return {
            title: appointment?.title || '',
            description: appointment?.description || '',
            startAt: appointment?.startAt ? moment(appointment?.startAt).set('second', 0).set('millisecond', 0) : '',
            endAt: appointment?.endAt ? moment(appointment?.endAt).set('second', 0).set('millisecond', 0) : '',
            agent: appointment?.calendars?.find((c) => c.type === 'agent')?.canonicalId || '',
            asset: appointment?.calendars?.find((c) => c.type === 'asset')?.canonicalId || '',
            status: appointment?.status || '',
            subStatus: appointment?.subStatus || '',
            comment: appointment?.comment || '',
            serviceId: appointment?.serviceId || '',
            extraFields: appointment?.extraFields || null,
            origin: appointment?.origin || '',
            lang: appointment?.lang || 'en',
            skipAvailabilityValidation: false,
        };
    }, [appointment]);

    const [fields, handleFieldChange, resetForm] = useFormFields(initialForm);

    useEffect(() => {
        if (appointment?.guests?.length) {
            setActors(appointment?.guests);
        }
    }, [appointment?.guests]);

    const [openCancelDlg, setOpenCancelDlg] = useState(false);

    const userDidChange = useCallback(() => {
        const { ...original } = initialForm;
        const { ...current } = fields;
        return !isEqual(original, current);
    }, [fields, initialForm]);

    const restart = useCallback(() => {
        setStep(1);
        resetForm(initialForm);
    }, [initialForm, resetForm]);

    const finalCancel = useCallback(() => {
        restart();
        dispatch(closeCustomDialogAction());
        dispatch(closeNestedDialogAction());
    }, [dispatch, restart]);

    const cancelDlg = (event) => {
        setOpenCancelDlg(false);
        dispatch(cancelCloseCustomDialogAction());
        dispatch(cancelCloseNestedDialogAction());
    };

    const manageCancel = useCallback(() => {
        if (!userDidChange()) {
            finalCancel();
        } else {
            setOpenCancelDlg(true);
        }
    }, [finalCancel, userDidChange]);

    const cancel = (event) => {
        event.preventDefault();
        manageCancel();
    };

    const handleSetSteep = (s) => {
        if (isValidForm()) {
            closeSubForm();
            setStep(s);
        }
    };

    const closeSubForm = () => {
        setSubForm(null);
    };

    const visibleSteps = [t('event.eventFormNav.settings'), t('event.eventFormNav.actors')];

    const openSubForm = (form) => {
        setSubForm(form);
    };

    const getGuestToSubmit = () => {
        return actors;
    };

    function handleSubmit(e) {
        e.preventDefault();
        onSubmit(
            {
                title: fields.title,
                description: fields.description,
                status: fields.status,
                subStatus: fields.subStatus,
                comment: fields.comment,
                extraFields: fields.extraFields,
                origin: fields.origin,
                lang: fields.lang,
                skipAvailabilityValidation: fields.skipAvailabilityValidation,
                calendarIds: [fields.agent, fields.asset].filter(Boolean),
                startAt: new Date(fields.startAt),
                endAt: new Date(fields.endAt),
                guests: getGuestToSubmit(),
                reminders,
                serviceId: fields.serviceId ? fields.serviceId : null,
            },
            () => {
                resetForm(initialForm);
            },
            () => enqueueSnackbar(t('app.common.error'), { variant: 'error' }),
        );
    }

    function handleSubmitDate(onSuccess, onFail) {
        onSubmit(
            {
                title: fields.title,
                description: fields.description,
                status: fields.status,
                subStatus: fields.subStatus,
                comment: fields.comment,
                extraFields: fields.extraFields,
                origin: fields.origin,
                lang: fields.lang,
                skipAvailabilityValidation: fields.skipAvailabilityValidation,
                guests: getGuestToSubmit(),
                reminders,
                serviceId: fields.serviceId ? fields.serviceId : null,
                calendarIds: [fields.agent, fields.asset].filter(Boolean),
                startAt: new Date(fields.startAt),
                endAt: new Date(fields.endAt),
            },
            (data) => {
                onSuccess();
                if (data?.id && data?.id !== appointment.id) {
                    dispatch(closeNestedDialogAction());
                    dispatch(closeCustomDialogAction());
                }
            },
            (error) => onFail(error?.response?.data),
            false,
        );
    }

    const isValidForm = () => {
        return Boolean(
            fields.title &&
                fields.agent &&
                fields.origin &&
                fields.startAt &&
                fields.endAt &&
                isBefore(new Date(fields.startAt), new Date(fields.endAt)),
        );
    };

    return (
        <LayoutForm
            title={action}
            customClasses={flexBox.vH70}
            confirmDlg={{
                open: openCancelDlg,
                accept: finalCancel,
                cancel: cancelDlg,
                titleDlg: confirmTitle,
                messageDlg: confirmMessage,
            }}
        >
            <Stack direction="column" spacing={2} className={cx(flexBox.overflowHidden, flexBox.h100)}>
                {!subForm && (
                    <Stack direction="column" spacing={3} className={cx(flexBox.overflowHidden, flexBox.h100)}>
                        <FormNav step={step} setStep={handleSetSteep} steps={visibleSteps} wizardMode={false} />

                        <Stack direction="column" className={cx(flexBox.overflowHidden, flexBox.h100)}>
                            {
                                {
                                    1: (
                                        <AppointmentFormSettings
                                            fields={fields}
                                            handleFieldChange={handleFieldChange}
                                            appointment={appointment}
                                            open={openSubForm}
                                            close={closeSubForm}
                                            setStep={setStep}
                                            step={step}
                                            cancel={cancel}
                                            onSubmitDate={handleSubmitDate}
                                            isValidForm={isValidForm}
                                            service={service}
                                            availabilitySelectedCalendars={availabilitySelectedCalendars}
                                            setAvailabilitySelectedCalendars={setAvailabilitySelectedCalendars}
                                        />
                                    ),
                                    2: (
                                        <AppointmentFormStep2
                                            fields={fields}
                                            handleFieldChange={handleFieldChange}
                                            step={step}
                                            setStep={handleSetSteep}
                                            cancel={cancel}
                                            actors={actors}
                                            setActors={setActors}
                                            reminders={reminders}
                                            setReminders={setReminders}
                                            onSubmit={handleSubmit}
                                        />
                                    ),
                                }[step]
                            }
                        </Stack>
                    </Stack>
                )}
                {subForm}
            </Stack>
        </LayoutForm>
    );
};

export default AppointmentForm;
